import { Component, DestroyRef, ViewChild, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ProfileService } from '../../../settings/account/profile/profile.service';
import { buildAccountPutPayload } from '../../../settings/account/profile/profile-payload.builder';
import { DeliveryInfoFormComponent, DeliveryFormState } from '../../components/delivery-info-form/delivery-info-form.component';
import { NotificationService } from '../../notification/notification.service';

@Component({
  selector: 'app-update-delivery-address',
  imports: [CommonModule, DeliveryInfoFormComponent],
  providers: [ProfileService],
  templateUrl: './update-delivery-address.component.html',
  styleUrl: './update-delivery-address.component.scss',
})
export class UpdateDeliveryAddressComponent {

  @ViewChild('deliveryFormChild') deliveryFormChild!: DeliveryInfoFormComponent;

  private activeModal = inject(NgbActiveModal);
  private profileService = inject(ProfileService);
  private notificationService = inject(NotificationService);
  private destroyRef = inject(DestroyRef);

  profileData = computed(() => this.profileService.profileData());
  formState = signal<DeliveryFormState>({ valid: true, isAddressUpdateValid: true, isDirty: false });
  isSaving = signal(false);

  constructor() {
    this.profileService.get().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.profileService.getMembershipOptions().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  onFormStateChange(state: DeliveryFormState) {
    this.formState.set(state);
  }

  save() {
    this.deliveryFormChild.markAllAsTouched();
    if (!this.deliveryFormChild.isValid()) {
      return this.notificationService.show({
        text: 'Please select your delivery information before saving.',
        type: 'error',
      });
    }

    const currentProfile = this.profileData();
    if (!currentProfile) {
      return this.notificationService.show({
        text: 'Profile data is not ready yet, please try again.',
        type: 'error',
      });
    }

    const deliverySlice = this.deliveryFormChild.buildDeliveryPayloadSlice();
    const payload = buildAccountPutPayload({
      profileData: currentProfile,
      deliverySlice,
    });

    this.isSaving.set(true);
    this.profileService.put(payload).pipe(
      tap(() => this.activeModal.close({ updated: true })),
      finalize(() => this.isSaving.set(false)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  cancel() {
    this.activeModal.close(null);
  }
}
